import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PiStorefrontLight } from 'react-icons/pi';
import Badge from '../shared/TailwindComponents/Badge';
import { isMarketplaceEnabled } from '../../utils/config';
import { MatterEngagement, getEngagementForMatter } from '../../services/api/marketplaceService';
import { enumLabel } from './marketplaceLabels';
import { fmtProposalPrice } from './marketplaceUi';
import { useLicenseGate } from '../../hooks/useLicenseGate';

/**
 * Matter header marker: "this matter came from a marketplace engagement".
 *
 * Renders nothing unless the build flag is on AND the backend has an
 * engagement for the matter (a 404 from a dark backend, or no engagement, is
 * silence - never an error on the matter page). Click → the request it came from.
 */
const MarketplaceOriginBadge: React.FC<{ matterUuid?: string }> = ({ matterUuid }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [origin, setOrigin] = useState<MatterEngagement | null>(null);
  /* Marketplace is its own section (Founding). Without it there is no
     engagement to show, and the read would only come back 403. */
  const { hasSectionAccess } = useLicenseGate();
  const marketplaceLicensed = hasSectionAccess('marketplace');

  useEffect(() => {
    if (!matterUuid || !isMarketplaceEnabled() || !marketplaceLicensed) return undefined;
    let alive = true;
    getEngagementForMatter(matterUuid)
      .then((e) => alive && setOrigin(e))
      .catch(() => alive && setOrigin(null));
    return () => {
      alive = false;
    };
  }, [matterUuid, marketplaceLicensed]);

  if (!origin || !marketplaceLicensed) return null;

  const price = `${enumLabel(t, origin.fee_terms.fee_basis)} · ${fmtProposalPrice(t, origin.fee_terms)}`;
  const escrow = origin.transaction
    ? t('Escrow {{state}}', { state: origin.transaction.escrow_state })
    : t('Billed per invoice');

  return (
    <button
      type="button"
      onClick={() => origin.lead_uuid && history.push(`/marketplace/inbox/${origin.lead_uuid}`)}
      className="inline-flex"
      data-testid="marketplace-origin-badge"
    >
      <Badge color="gray" icon={<PiStorefrontLight className="h-3.5 w-3.5" />}
        tooltip={`${t('Marketplace engagement')} · ${price} · ${escrow}`}>
        {t('Marketplace')}
      </Badge>
    </button>
  );
};

export default MarketplaceOriginBadge;
